import { Component, EventEmitter, inject, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { ErrorCodeQuery } from '../../../../../shared/models/error-code/error-code-query';
import { Policy } from '../../../../../shared/models/info/policy';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { IfPolicySatisfiedDirective } from '../../../../directives/if-policy-satisfied.directive';
import { SearchIconComponent } from '../../../icons/search-icon/search-icon.component';
import { RestoreIconComponent } from '../../../icons/restore-icon/restore-icon.component';
import { IfPolicyNotSatisfiedDirective } from '../../../../directives/if-policy-not-satisfied.directive';
import { FilterIconComponent } from '../../../icons/filter-icon/filter-icon.component';
import { ErrorCodeFilterComponent } from '../error-code-filter/error-code-filter.component';
import { HelpPopoverComponent } from '../../../common/popover/help-popover/help-popover.component';
import { Paragraph } from '../../../../../shared/models/internal/popover/paragraph';
import { DomSanitizer } from '@angular/platform-browser';
import { buildTranslationStateColorsHelp } from '../../../../../shared/internal/build-translation-state-colors-help';
import { LanguageSelectionComponent } from '../../../common/language/language-selection/language-selection.component';
import { AgentActionDirective } from '../../../../directives/agent-action.directive';

@Component({
  selector: 'app-error-code-table-search',
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    IfPolicySatisfiedDirective,
    IfPolicyNotSatisfiedDirective,
    SearchIconComponent,
    RestoreIconComponent,
    FilterIconComponent,
    ErrorCodeFilterComponent,
    HelpPopoverComponent,
    LanguageSelectionComponent,
    AgentActionDirective,
  ],
  templateUrl: './error-code-table-search.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './error-code-table-search.component.scss',
})
export class ErrorCodeTableSearchComponent implements OnInit {
  @Input() public restoreDisabled = false;
  @Input() public recyclingModeEnabled = false;
  @Output() public searchTriggered: EventEmitter<ErrorCodeQuery> = new EventEmitter<ErrorCodeQuery>();

  public filterVisible = false;
  public randomId = crypto.randomUUID();
  public searchQuery: ErrorCodeQuery = {
    searchPattern: '',
  } as ErrorCodeQuery;
  public policy = Policy;
  public paragraphs: Paragraph[] = [];

  private readonly sanitizer = inject(DomSanitizer);

  public ngOnInit(): void {
    this.paragraphs = buildTranslationStateColorsHelp(this.sanitizer);

    this.search();
  }

  public search(): void {
    this.searchTriggered.emit(this.searchQuery);
  }

  public searchByKey(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.search();
    } else if (event.key === 'Escape') {
      this.clearSearch();
    }
  }

  public toggleFilter(): void {
    this.filterVisible = !this.filterVisible;
  }

  public filtersAreSet(): boolean {
    return (
      this.searchQuery.searchInCode === false ||
      this.searchQuery.searchInMessage === false ||
      this.searchQuery.needsReview === true ||
      this.searchQuery.changesBy !== undefined ||
      this.searchQuery.causeInternNotEmpty !== undefined ||
      this.searchQuery.causeDealerNotEmpty !== undefined ||
      this.searchQuery.causeFarmerNotEmpty !== undefined ||
      this.searchQuery.remedyInternNotEmpty !== undefined ||
      this.searchQuery.remedyDealerNotEmpty !== undefined ||
      this.searchQuery.remedyFarmerNotEmpty !== undefined ||
      this.searchQuery.searchInMachineTypes === true ||
      this.searchQuery.mainLanguageDocumentationFieldsExportedForReview === true ||
      this.searchQuery.mainLanguageDocumentationFieldsReviewed !== undefined ||
      this.searchQuery.searchInDocumentationFields === true ||
      (this.searchQuery.translationStatesDocumentation?.length ?? 0) > 0 ||
      (this.searchQuery.translationStatesTerminal?.length ?? 0) > 0
    );
  }

  public filterApplied(query: ErrorCodeQuery): void {
    query.searchPattern = this.searchQuery.searchPattern;
    query.page = this.searchQuery.page;
    query.pageSize = this.searchQuery.pageSize;
    query.orderBy = this.searchQuery.orderBy;
    query.orderByDescending = this.searchQuery.orderByDescending;
    query.softDeleted = this.searchQuery.softDeleted;
    query.nwNumber = this.searchQuery.nwNumber;
    query.codes = this.searchQuery.codes;
    this.searchQuery = { ...query };
    this.searchTriggered.emit(this.searchQuery);
  }

  private clearSearch(): void {
    this.searchQuery.searchPattern = '';
    this.searchTriggered.emit(this.searchQuery);
  }
}
